/**
 * Feature Flag Service
 * 
 * Centralized feature flag management with hybrid approach:
 * - Environment variables (build-time, always available)
 * - Unleash integration (runtime, optional)
 * - localStorage caching (offline resilience)
 * 
 * Flag Resolution Priority:
 * 1. Unleash remote (if connected)
 * 2. localStorage cache (last known Unleash state)
 * 3. Environment variables (build-time fallback)
 * 4. Default value
 * 
 * Flag Naming Convention:
 * - GLOBAL_*: Cross-cutting features (e.g., GLOBAL_SIGNALR_REALTIME)
 * - {PILET}_*: Pilet-specific features (e.g., CAMERA_AI_SEARCH)
 * - {CONTEXT}_*: Context-scoped features (e.g., PARTNER_CUSTOMER_ALERTS)
 */

/**
 * Feature flag provider interface for abstraction
 */
export interface IFeatureFlagProvider {
  /**
   * Initialize the provider (async)
   */
  initialize(): Promise<void>;
  
  /**
   * Check if a flag is enabled
   */
  isEnabled(flagKey: string, context?: FlagEvaluationContext): boolean;
  
  /**
   * Get all flags as key-value pairs
   */
  getAllFlags(): Record<string, boolean>;
  
  /**
   * Check if provider is ready/connected
   */
  isReady(): boolean;
}

/**
 * Context for flag evaluation (targeting)
 */
export interface FlagEvaluationContext {
  userId?: string;
  context?: 'PARTNER' | 'RESIDENTIAL' | 'COMMERCIAL';
  role?: string;
  permissions?: string[];
}

/**
 * Targeting options for flag checks
 */
export interface FlagTargetingOptions {
  context?: 'PARTNER' | 'RESIDENTIAL' | 'COMMERCIAL';
  role?: string;
  userId?: string;
  permission?: string;
}

/**
 * Environment Variable Provider
 * Reads feature flags from Vite environment variables
 */
export class EnvVarProvider implements IFeatureFlagProvider {
  private flags: Record<string, boolean> = {};
  private ready = false;
  
  async initialize(): Promise<void> {
    this.flags = this.loadEnvVarFlags();
    this.ready = true;
  }
  
  isEnabled(flagKey: string): boolean {
    return this.flags[flagKey] ?? false;
  }
  
  getAllFlags(): Record<string, boolean> {
    return { ...this.flags };
  }
  
  isReady(): boolean {
    return this.ready;
  }
  
  /**
   * Load all VITE_FLAG_* environment variables
   */
  private loadEnvVarFlags(): Record<string, boolean> {
    const flags: Record<string, boolean> = {};
    
    // Parse all VITE_FLAG_* and VITE_FEATURE_* variables
    Object.keys(import.meta.env).forEach((key) => {
      let flagKey: string | null = null;
      
      // New convention: VITE_FLAG_*
      if (key.startsWith('VITE_FLAG_')) {
        flagKey = key.replace('VITE_FLAG_', '');
      }
      // Legacy convention: VITE_FEATURE_*
      else if (key.startsWith('VITE_FEATURE_')) {
        flagKey = key.replace('VITE_FEATURE_', '');
      }
      
      if (flagKey) {
        const value = import.meta.env[key];
        flags[flagKey] = value === 'true' || value === true;
      }
    });
    
    return flags;
  }
}

/**
 * Unleash Provider (Future Implementation)
 * 
 * TODO: Implement Unleash SDK integration
 * - Install: npm install @unleash/proxy-client-react
 * - Initialize UnleashClient with URL and client key
 * - Bootstrap with cached flags
 * - Poll for updates every 30s
 * - Save to localStorage on updates
 * - Handle offline/error scenarios
 */
export class UnleashProvider implements IFeatureFlagProvider {
  private ready = false;
  private flags: Record<string, boolean> = {};
  
  async initialize(): Promise<void> {
    console.warn('UnleashProvider not yet implemented. Using environment variables only.');
    // TODO: Initialize Unleash SDK
    // const client = new UnleashClient({ ... });
    // await client.start();
    this.ready = false;
  }
  
  isEnabled(flagKey: string): boolean {
    return this.flags[flagKey] ?? false;
  }
  
  getAllFlags(): Record<string, boolean> {
    return { ...this.flags };
  }
  
  isReady(): boolean {
    return this.ready;
  }
}

/**
 * Feature Flag Service
 * Coordinates multiple providers with layered fallback
 */
export class FeatureFlagService {
  private providers: IFeatureFlagProvider[] = [];
  private envVarProvider: EnvVarProvider;
  private cacheKey = 'oneportal:feature-flags:cache';
  private initialized = false;
  
  constructor() {
    // Always have env var provider as baseline
    this.envVarProvider = new EnvVarProvider();
    this.providers.push(this.envVarProvider);
  }
  
  /**
   * Initialize feature flag service
   * Non-blocking: App can start with env vars while providers initialize
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.warn('FeatureFlagService already initialized');
      return;
    }
    
    console.log('🎌 Initializing feature flag service...');
    
    // Initialize env var provider (synchronous)
    await this.envVarProvider.initialize();
    console.log('📋 Environment variable flags loaded');
    
    // TODO: Initialize Unleash provider if configured
    const unleashUrl = import.meta.env.VITE_UNLEASH_URL;
    const unleashKey = import.meta.env.VITE_UNLEASH_CLIENT_KEY;
    
    if (unleashUrl && unleashKey) {
      console.log('🚀 Unleash configuration detected (implementation pending)');
      // TODO: Add UnleashProvider to providers array
      // const unleashProvider = new UnleashProvider();
      // await unleashProvider.initialize();
      // this.providers.unshift(unleashProvider); // Higher priority than env vars
    }
    
    this.initialized = true;
    console.log('✅ Feature flag service initialized');
  }
  
  /**
   * Check if a feature flag is enabled
   * 
   * @param flagKey - Flag name (e.g., "CAMERA_AI_SEARCH", "PARTNER_CUSTOMER_ALERTS")
   * @param targetingOptions - Optional targeting criteria
   * @returns true if flag is enabled and passes targeting checks
   */
  isEnabled(flagKey: string, targetingOptions?: FlagTargetingOptions): boolean {
    // Check providers in priority order (first provider wins)
    for (const provider of this.providers) {
      if (provider.isReady()) {
        const baseEnabled = provider.isEnabled(flagKey);
        
        if (!baseEnabled) {
          return false; // Flag disabled at base level
        }
        
        // Apply targeting rules if specified
        if (targetingOptions && !this.evaluateTargeting(targetingOptions)) {
          return false; // Targeting criteria not met
        }
        
        return true;
      }
    }
    
    // Fallback to cache if no providers ready
    const cached = this.loadFromCache();
    return cached[flagKey] ?? false;
  }
  
  /**
   * Get all flags as object
   * Merges flags from all providers (higher priority providers override lower)
   */
  getAllFlags(): Record<string, boolean> {
    let flags: Record<string, boolean> = {};
    
    // Start with cache
    flags = { ...this.loadFromCache() };
    
    // Override with providers (reverse order so higher priority overwrites)
    for (let i = this.providers.length - 1; i >= 0; i--) {
      const provider = this.providers[i];
      if (provider.isReady()) {
        flags = { ...flags, ...provider.getAllFlags() };
      }
    }
    
    return flags;
  }
  
  /**
   * Check if flag is enabled for specific pilet
   * Uses pilet namespace: PILET_NAME_FEATURE_NAME
   */
  isPiletFeatureEnabled(
    piletName: string,
    featureName: string,
    targetingOptions?: FlagTargetingOptions
  ): boolean {
    const flagKey = `${piletName.toUpperCase()}_${featureName}`;
    return this.isEnabled(flagKey, targetingOptions);
  }
  
  /**
   * Check if flag is enabled for specific context
   * Uses context namespace: PARTNER_FEATURE or CONSUMER_FEATURE
   */
  isContextFeatureEnabled(
    context: 'PARTNER' | 'RESIDENTIAL' | 'COMMERCIAL',
    featureName: string,
    targetingOptions?: FlagTargetingOptions
  ): boolean {
    const flagKey = `${context}_${featureName}`;
    return this.isEnabled(flagKey, targetingOptions);
  }
  
  /**
   * Evaluate targeting criteria
   * All specified criteria must pass (AND logic)
   */
  private evaluateTargeting(options: FlagTargetingOptions): boolean {
    // NOTE: This is client-side targeting for simple cases
    // For production, consider server-side targeting via Unleash
    
    // Context targeting would be checked here with auth context
    // Role targeting would be checked here with auth context
    // Permission targeting would be checked here with auth context
    // User ID targeting would be checked here with auth context
    
    // For now, return true (no targeting enforcement)
    // This will be enhanced when auth context integration is added
    return true;
  }
  
  /**
   * Load flags from localStorage cache
   */
  private loadFromCache(): Record<string, boolean> {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.warn('Failed to load cached flags:', error);
      return {};
    }
  }
  
  /**
   * Save flags to localStorage cache
   */
  saveToCache(flags: Record<string, boolean>): void {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(flags));
    } catch (error) {
      console.warn('Failed to cache flags:', error);
    }
  }
}

// Singleton instance
export const featureFlagService = new FeatureFlagService();
